import { CalendarToday, HeartBroken, PetsOutlined } from "@mui/icons-material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GoBack from "../../../../../../components/buttons/GoBack";
import { PRIMARY_BLUE } from "../../../../../../enums/Colors";
import useStringTranslation from "../../../../../../hooks/useStringTranslation";
import { calculateAge } from "../../../../../pets/utils/Functions";
import { DialogText } from "../../../../components/core/DialogCore";
import { nonOverflowingTitleSx } from "../../../../../../enums/Styles";
import { BsFillBox2HeartFill } from "react-icons/bs";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import PetAvatar from "../../../../../pets/singleView/display/PetAvatar";
import useUser from "../../../../../../hooks/useUser";
import { trimText } from "../../../../../../utils/Functions";
import useCanUserEditPet from "../../../../../pets/hooks/useCanUserEditPet";
import PetHeaderActions from "./PetHeaderActions";

export default function PetHeader({ pet = {} }) {
  return (
    <Stack spacing={4}>
      <BackNavigation pet={pet} />
      <PetProfile pet={pet} />
    </Stack>
  );
}

function BackNavigation({ pet = {} }) {
  return (
    <Stack direction="row" alignItems="center" sx={{ mb: 4 }}>
      <GoBack ignoreParams testId="pet.header.go-back" />
      <Stack direction="row" spacing={2} sx={{ marginLeft: "auto" }}>
        <PetHeaderActions pet={pet} />
      </Stack>
    </Stack>
  );
}

function PetProfile({ pet = {} }) {
  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={4}
        alignItems={{ xs: "center", md: "flex-start" }}
        sx={{ position: "relative" }}
      >
        <HeaderAvatar pet={pet} />
        <IconInformation pet={pet} />
      </Stack>
      <Stack spacing={2}>
        <PetDescription pet={pet} />
        <PetAggression pet={pet} />
      </Stack>
    </>
  );
}

function HeaderAvatar({ pet = {} }) {
  const canUserEditPet = useCanUserEditPet(pet);
  return (
    <Box sx={{ position: "relative" }}>
      <PetAvatar
        addAvatar={canUserEditPet && pet?.active && !pet?.deceased}
        size={140}
      />
    </Box>
  );
}

function PetDescription({ pet = {} }) {
  const { t } = useTranslation();
  if (!pet || !pet.description) return null;
  const title = `${t("About")} ${pet?.name}`;
  return (
    <DialogText
      title={t("About")}
      text={pet?.description}
      dialogTitle={title}
      key={"pet_description_key"}
      testId="pet.header.about"
    />
  );
}

// Aggression is clinic-facing information, hidden from the pet's owner
function PetAggression({ pet = {} }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  if (roles.user || !pet?.aggression) return null;
  return (
    <DialogText
      title={t("Aggression")}
      text={pet.aggression}
      dialogTitle={`${t("Aggression")} - ${pet?.name}`}
      key={"pet_aggression_key"}
      testId="pet.header.aggression"
    />
  );
}

const iconColor = "secondary";

function IconInformation({ pet = {} }) {
  const { t } = useTranslation();
  const species = useStringTranslation(pet?.type, "title");

  return (
    <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          mb: 2,
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <Typography
          title={pet?.name}
          variant="h2"
          data-testid="pet.header.name"
          sx={{
            ...nonOverflowingTitleSx,
            fontWeight: 800,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
          }}
        >
          {trimText(pet?.name, 30)}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        mb={4}
        justifyContent={{ xs: "center", md: "flex-start" }}
      >
        {pet?.deceased && (
          <ChipIcon
            icon={<HeartBroken sx={{ color: "seashell" }} />}
            title={t("DeceasedPet")}
            bgcolor="error.main"
            testId="pet.header.chip.deceased"
          />
        )}

        {pet?.boxName && (
          <ChipIcon
            icon={<BsFillBox2HeartFill color={iconColor} />}
            title={`${t("Box")}: ${pet.boxName}`}
            testId="pet.header.chip.box"
          />
        )}

        {pet?.numberOfVaccines != null && (
          <ChipIcon
            icon={<VaccinesIcon color={iconColor} />}
            title={`${t("VACCINATED")}: ${
              pet.numberOfVaccines > 0 ? t("Yes") : t("No")
            }`}
            testId="pet.header.chip.vaccinated"
          />
        )}

        <ChipIcon
          icon={<PetsOutlined color={iconColor} />}
          title={`${t("Species")}: ${species}`}
          testId="pet.header.chip.species"
        />

        {pet?.breed && (
          <ChipIcon
            icon={<PetsOutlined color={iconColor} />}
            title={`${t("Breed")}: ${t(pet.breed)}`}
            testId="pet.header.chip.breed"
          />
        )}

        {pet?.birthdate && (
          <ChipIcon
            icon={<CalendarToday color={iconColor} />}
            title={`${t("Age")}: ${calculateAge(pet.birthdate, t)}`}
            testId="pet.header.chip.age"
          />
        )}

        <Ownership pet={pet} />
      </Stack>
    </Box>
  );
}

function ChipIcon({ title = "", icon = null, bgcolor, testId }) {
  if (!title && !icon) return null;

  return (
    <IconWrapper bgcolor={bgcolor} testId={testId}>
      {icon}
      <Typography variant="body1" textTransform="capitalize" color="seashell">
        {title}
      </Typography>
    </IconWrapper>
  );
}

function Ownership({ pet = {} }) {
  const { t } = useTranslation();
  if (pet?.userId) {
    return (
      <IconWrapper testId="pet.header.chip.owner">
        <PersonIcon color={iconColor} />
        <Typography variant="body1" color={"seashell"}>
          {t("Owner")}: <strong>{pet?.ownerName}</strong>
        </Typography>
      </IconWrapper>
    );
  } else if (pet?.clinicId) {
    return (
      <IconWrapper testId="pet.header.chip.clinic">
        <LocalHospitalIcon color={iconColor} />
        <Typography variant="body1" color={"seashell"}>
          {t("Clinic")}:{" "}
          <strong>{String(pet?.clinicShortName).toUpperCase()}</strong>
        </Typography>
      </IconWrapper>
    );
  }
  return null;
}

function IconWrapper({ children, bgcolor = PRIMARY_BLUE, testId }) {
  return (
    <Stack
      data-testid={testId}
      direction="row"
      alignItems="center"
      spacing={1}
      bgcolor={bgcolor}
      p={1}
      pl={2}
      pr={2}
      borderRadius={8}
    >
      {children}
    </Stack>
  );
}
